import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import { useNavLayout } from '../../lib/NavLayoutContext';
import { Toaster } from 'sonner';
import ThemeToggle from '../ThemeToggle';
import PortalBrandMark from '../branding/PortalBrandMark';
import PreviewBanner from '../branding/PreviewBanner';
import { isPreviewOverrideActive } from '../../lib/branding';
import { LegacyTransitionBanner } from '../../operationalBase/components/LegacyTransitionBanner';
import { useDemoMode } from '../../operationalBase/demoMode/DemoModeContext';
import DemoUnavailable from '../../operationalBase/demoMode/DemoUnavailable';
import BroadcastSurfaces from '../broadcasts/BroadcastSurfaces';
import InsightToggle from '../../insight/InsightToggle';
import HelperLauncher from '../helper/HelperLauncher';
import { demoSlot } from '../../lib/session';
import LanguageToggle from '../LanguageToggle';
import MedDemoReset from '../MedDemoReset';

const TOP_NAV_BREAKPOINT = 1024;

const Layout = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const { active: demoActive, loaded: demoLoaded } = useDemoMode();
    const { navLayout } = useNavLayout();

    // v4.34.13 — track viewport. Top-nav mode only applies at
    // ≥ 1024 px; below that the sidebar drawer is the only sane
    // mobile navigation pattern, regardless of preference.
    const [isWide, setIsWide] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth >= TOP_NAV_BREAKPOINT,
    );
    useEffect(() => {
        const onResize = () => setIsWide(window.innerWidth >= TOP_NAV_BREAKPOINT);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const useTopNav = navLayout === 'top' && isWide;

    /* v4.30.1 — track preview override so we can offset the fixed
     * top command bar, sidebar, and mobile header vertically when
     * the preview banner is showing. The offset is exposed via a
     * `--preview-offset` CSS variable on :root so every fixed
     * element in the shell can opt in without prop-drilling. */
    const [previewActive, setPreviewActive] = useState(() => isPreviewOverrideActive());
    useEffect(() => {
        const sync = () => setPreviewActive(isPreviewOverrideActive());
        window.addEventListener('ob:preview-override-changed', sync);
        return () => window.removeEventListener('ob:preview-override-changed', sync);
    }, []);
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        document.documentElement.style.setProperty(
            '--preview-offset',
            previewActive ? '28px' : '0px'
        );
        return () => {
            document.documentElement.style.setProperty('--preview-offset', '0px');
        };
    }, [previewActive]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="spinner" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Showroom demo manager: render the EXISTING Command Center ONLY — no
    // portal sidebar, no top nav, no other modules, no Help/Insights — and
    // force dark/night. Strictly scoped to the synthetic demo tab via the
    // sessionStorage flag set by DemoManagerEntry; real users are never
    // affected. Does not alter Command Center's own design.
    const isShowroomDemo = typeof window !== 'undefined'
        && window.sessionStorage
        && window.sessionStorage.getItem('yulontap_demo_mode') === '1';
    if (isShowroomDemo) {
        const demoRet = (typeof window !== 'undefined' && window.sessionStorage.getItem('yulontap_demo_return')) || '';
        const goTenant = () => {
            const q = demoRet ? `?return_url=${encodeURIComponent(demoRet)}` : '';
            window.location.href = `/demo/tenant${q}`;
        };
        const isAuto = typeof window !== 'undefined' && window.sessionStorage
            && window.sessionStorage.getItem('yulontap_auto_demo') === '1';
        const isHotel = typeof window !== 'undefined' && window.sessionStorage
            && window.sessionStorage.getItem('yulontap_hotel_demo') === '1';
        // Which medical persona this tab was minted as ('coordinator' |
        // 'head-office'); absent for every other demo, so no other shell moves.
        const medPersona = typeof window !== 'undefined' && window.sessionStorage
            ? window.sessionStorage.getItem('yulontap_med_demo_persona') : null;
        if (medPersona) {
            // Medical demo shell — HEADER BRANDING ONLY, for the same reason the
            // hotel branch exists: without it a clinic fell through to the
            // PROPERTY header and showed the Maple Properties logo and "View as
            // Tenant" on a clinic screen. Patient / coordinator / head office,
            // nothing else. Command Center layout, data and logic untouched.
            const medPersonas = [
                { key: 'patient', to: '/demo/medical/patient', label: t('demoMed.viewAsPatient', 'View as Patient'), short: t('demoMed.shortPatient', 'Patient'), cls: 'bg-emerald-600/90 hover:bg-emerald-500 border-emerald-300/30', show: true },
                { key: 'coordinator', to: '/demo/medical/coordinator', label: t('demoMed.viewAsCoordinator', 'View as Coordinator'), short: t('demoMed.shortCoordinator', 'Coordinator'), cls: 'bg-blue-600/90 hover:bg-blue-500 border-blue-300/30', show: medPersona !== 'coordinator' },
                { key: 'head-office', to: '/demo/medical/head-office', label: t('demoMed.viewAsHeadOffice', 'View as Head Office'), short: t('demoMed.shortHeadOffice', 'Head office'), cls: 'bg-orange-600/90 hover:bg-orange-500 border-orange-300/30', show: medPersona !== 'head-office' },
            ];
            return (
                <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="demo-manager-shell">
                    <div className="flex flex-nowrap items-center justify-between gap-2 px-3 sm:px-5 py-3 border-b border-white/10 bg-slate-950/80 backdrop-blur">
                        <span className="text-sm font-semibold text-white tracking-wide truncate min-w-0" data-testid="demo-med-brand">
                            {t('demoMed.brand', 'Victoria Park Medispa')}
                        </span>
                        <div className="flex flex-nowrap items-center gap-2 sm:gap-3 shrink-0">
                            <div className="w-[128px] sm:w-[150px] shrink-0" data-testid="demo-language-picker">
                                <LanguageToggle variant="compact" surface="demo" />
                            </div>
                            <MedDemoReset variant="inline" />
                            <div className="flex flex-nowrap items-center gap-2 shrink-0" data-testid="demo-med-view-switch">
                                {medPersonas.filter((p) => p.show).map((p) => (
                                    <button key={p.key} onClick={() => { window.location.href = p.to; }}
                                        data-testid={`demo-view-as-${p.key}`}
                                        title={p.label} aria-label={p.label}
                                        className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-white border rounded-full px-3 py-1.5 transition-colors ${p.cls}`}>
                                        <span className="hidden sm:inline">{p.label}</span>
                                        <span className="sm:hidden">{p.short}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <main className="min-h-screen">
                        <div key={location.pathname} className="page-container yp-page">
                            {children}
                        </div>
                    </main>
                    <Toaster position="top-right" richColors closeButton
                        toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
                </div>
            );
        }
        if (isHotel && !isAuto) {
            // Hotel demo shell — HEADER BRANDING ONLY. Without this branch the
            // hotel demo fell through to the PROPERTY header and showed the
            // Maple Properties logo + "View as Tenant" on the hotel demo. The
            // hotel equivalent of the tenant is the GUEST; role switching for
            // the staff departments already lives in HotelDemoSwitcher, so
            // nothing new is invented here. Command Center layout/data/logic
            // are untouched.
            return (
                <div className="min-h-screen bg-slate-950" data-testid="demo-manager-shell">
                    <div className="flex flex-nowrap items-center justify-between gap-2 px-3 sm:px-5 py-3 border-b border-white/10 bg-slate-950/80 backdrop-blur">
                        <span className="text-sm font-semibold text-white tracking-wide truncate min-w-0" data-testid="demo-hotel-brand">
                            {t('hotelDemo.brand', 'Grand Demo Hotel')}
                        </span>
                        <div className="flex flex-nowrap items-center gap-2 sm:gap-3 shrink-0">
                            <div className="w-[128px] sm:w-[150px] shrink-0" data-testid="demo-language-picker">
                                <LanguageToggle variant="compact" surface="demo" />
                            </div>
                            {/* One line at every width: short label under sm, full
                                label (and tooltip) from sm up. */}
                            <button
                                onClick={() => { window.location.href = '/demo/hotel/guest'; }}
                                data-testid="demo-view-as-guest"
                                title={t('hotelDemo.viewAsGuest', 'View as Guest')}
                                aria-label={t('hotelDemo.viewAsGuest', 'View as Guest')}
                                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-white bg-amber-600/90 hover:bg-amber-500 border border-amber-300/30 rounded-full px-3 py-1.5 transition-colors">
                                <span className="hidden sm:inline">{t('hotelDemo.viewAsGuest', 'View as Guest')}</span>
                                <span className="sm:hidden">{t('hotelDemo.viewAsGuestShort', 'Guest')}</span>
                            </button>
                        </div>
                    </div>
                    <main className="min-h-screen">
                        <div key={location.pathname} className="page-container yp-page">
                            {children}
                        </div>
                    </main>
                    <Toaster position="top-right" richColors closeButton
                        toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
                </div>
            );
        }
        if (isAuto) {
            // Automotive demo shell — mirrors property's "View as Tenant" pattern
            // (top-right direct-view buttons), generalized to the 3 automotive
            // personas. Same-tab, relative paths. The persona for the current
            // page is hidden. Fully trilingual.
            const path = location.pathname;
            // Inside a split-screen pane these buttons would re-navigate that
            // pane and break the split, and they duplicate the split view's own
            // toggle. The SHELL still renders — only the bar goes.
            const inPane = !!demoSlot();
            const personas = [
                { key: 'customer', to: '/demo/automotive/buyer', label: t('demoAuto.viewAsCustomer', 'View as Customer'), short: t('demoAuto.shortCustomer', 'Customer'), cls: 'bg-emerald-600/90 hover:bg-emerald-500 border-emerald-300/30', show: !path.startsWith('/vehicle/') },
                { key: 'sales', to: '/demo/automotive/salesperson', label: t('demoAuto.viewAsSalesperson', 'View as Salesperson'), short: t('demoAuto.shortSalesperson', 'Salesperson'), cls: 'bg-blue-600/90 hover:bg-blue-500 border-blue-300/30', show: path !== '/vehicle-leads' && path !== '/sales-desk' },
                { key: 'manager', to: '/demo/automotive/command-center', label: t('demoAuto.viewAsManagerCc', 'View as Manager'), short: t('demoAuto.shortManager', 'Manager'), cls: 'bg-orange-600/90 hover:bg-orange-500 border-orange-300/30', show: path !== '/command-center' },
            ];
            return (
                <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="demo-manager-shell">
                    <div className={`flex flex-nowrap items-center justify-between gap-2 px-3 sm:px-5 py-3 border-b border-white/10 bg-slate-950/80 backdrop-blur ${inPane ? 'hidden' : ''}`}>
                        <span className="text-sm font-semibold text-white tracking-wide truncate min-w-0" data-testid="demo-auto-brand">Prestige Auto</span>
                        {/* One line at every width: short label under sm, full label
                            (and tooltip) from sm up — never wraps, never overflows. */}
                        <div className="flex flex-nowrap items-center gap-2 shrink-0" data-testid="demo-auto-view-switch">
                            {personas.filter((p) => p.show).map((p) => (
                                <button key={p.key} onClick={() => { window.location.href = p.to; }}
                                    data-testid={`demo-view-as-${p.key}`}
                                    title={p.label} aria-label={p.label}
                                    className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-white border rounded-full px-3 py-1.5 transition-colors ${p.cls}`}>
                                    <span className="hidden sm:inline">{p.label}</span>
                                    <span className="sm:hidden">{p.short}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <main className="min-h-screen">
                        <div key={location.pathname} className="page-container yp-page">
                            {children}
                        </div>
                    </main>
                    <Toaster position="top-right" richColors closeButton
                        toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
                </div>
            );
        }
        return (
            <div className="min-h-screen bg-slate-950" data-testid="demo-manager-shell">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-slate-950/80 backdrop-blur">
                    <img src="/maple-properties-logo.png" alt="Maple Court Properties"
                        data-testid="maple-logo"
                        className="h-10 w-auto object-contain" />
                    <div className="flex items-center gap-3">
                        <div className="w-[150px]" data-testid="demo-language-picker">
                            <LanguageToggle variant="compact" surface="demo" />
                        </div>
                        <button
                            onClick={goTenant}
                            data-testid="demo-view-as-tenant"
                            title={t('propertyDemo.viewAsTenant', 'View as Tenant')}
                            aria-label={t('propertyDemo.viewAsTenant', 'View as Tenant')}
                            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-white bg-emerald-600/90 hover:bg-emerald-500 border border-emerald-300/30 rounded-full px-3 py-1.5 transition-colors">
                            <span className="hidden sm:inline">{t('propertyDemo.viewAsTenant', 'View as Tenant')}</span>
                            <span className="sm:hidden">{t('propertyDemo.viewAsTenantShort', 'Tenant')}</span>
                        </button>
                    </div>
                </div>
                <main className="min-h-screen">
                    <div key={location.pathname} className="page-container yp-page">
                        {children}
                    </div>
                </main>
                <Toaster
                    position="top-right"
                    richColors
                    closeButton
                    toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }}
                />
            </div>
        );
    }

    // v4.8.1 — Demo Mode route guard.
    if (demoLoaded && demoActive) {
        return <DemoUnavailable />;
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200"
             style={{ paddingRight: 'var(--helper-dock-w, 0px)' }}>
            <PreviewBanner />
            {/* Phase 2 — Broadcast Center sticky + modal surfaces.
              * Inline surfaces are served by the Welcome route directly. */}
            <BroadcastSurfaces />
            {useTopNav ? (
                // v4.34.13 — Top-nav shell. Replaces both the sidebar
                // AND the fixed top command bar. InsightToggle is
                // intentionally available via the Help & Insights
                // menu inside the top nav, matching the sidebar.
                <div style={{ paddingTop: 'var(--preview-offset, 0px)' }}>
                    <TopNav />
                </div>
            ) : (
                <>
                    <Sidebar />
                    {/* Top Command Bar for mobile + Theme Toggle */}
                    <div
                        className="fixed left-0 lg:left-64 h-16 lg:h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 z-30"
                        style={{ top: 'var(--preview-offset, 0px)',
                                 right: 'var(--helper-dock-w, 0px)' }}
                    >
                        <div className="hidden lg:flex items-center" data-testid="layout-topbar-brand-slot">
                            <PortalBrandMark
                                variant="topbar"
                                showLabel={false}
                                fallbackToPlatform={false}
                            />
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                            <HelperLauncher variant="light" />
                            <InsightToggle variant="light" />
                            <ThemeToggle />
                        </div>
                    </div>
                </>
            )}
            <main
                className={useTopNav ? 'min-h-screen' : 'lg:ml-64 min-h-screen pt-16 lg:pt-14'}
                style={useTopNav ? undefined : { marginTop: 'var(--preview-offset, 0px)' }}
            >
                <LegacyTransitionBanner />
                <div key={location.pathname} className="page-container yp-page">
                    {children}
                </div>
            </main>
            <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                    style: {
                        fontFamily: 'DM Sans, sans-serif',
                    },
                }}
            />
        </div>
    );
};

export default Layout;
